import {
  type RefObject,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  ComboBox as AriaComboBox,
  Input as AriaInput,
  ListBox as AriaListBox,
  ListBoxItem as AriaListBoxItem,
  OverlayTriggerStateContext,
  Popover as AriaPopover,
} from "react-aria-components";
import "./hero-combobox.css";

const INITIAL_VISIBLE_RESULTS = 6;
const SEARCH_VISIBLE_RESULTS = 6;
const AREA_CHILD_SCORE = 50;

/**
 * Generic option shape for the HeroComboBox.
 * Works for airports, cities, venues, or any searchable entity.
 */
export interface HeroComboBoxOption {
  id: string;
  label: string;
  /** Optional short code displayed prominently (e.g. airport IATA code, country code) */
  code?: string;
  /** Optional secondary description shown below the label */
  description?: string;
  /** Whether this is an area-wide airport group or an individual AIRPORT */
  type?: "AREA" | "AIRPORT";
  /** For AIRPORT entries: the parent area code this belongs to (e.g. "LON" for LHR) */
  parentMetroCode?: string;
  /** For AREA entries: the codes of constituent airports */
  constituentAirports?: string[];
}

interface HeroComboBoxDisplayOption extends HeroComboBoxOption {
  isGroupedChild?: boolean;
}

interface HeroComboBoxProps {
  /** Optional label shown above the input (e.g. "Departing", "Arriving") */
  label?: string;
  /** Colored label shown below the input (e.g. "Origin", "Destination") */
  sublabel: string;
  /** List of selectable options */
  options: HeroComboBoxOption[];
  /** Optional options to show first when the input is empty */
  featuredOptions?: HeroComboBoxOption[];
  /** Whether to show options before the user enters a search query */
  showOptionsOnEmpty?: boolean;
  /** Whether selecting an option is required */
  isRequired?: boolean;
  /** Current visible label value */
  value: string;
  /** Current selected destination code */
  selectedCode?: string;
  /** Called when selection changes */
  onChange: (option: HeroComboBoxOption | null) => void;
  /** Placeholder text when input is empty */
  placeholder?: string;
  /** Accessible label for screen readers */
  "aria-label"?: string;
  /** ID of visible text that labels the combobox */
  "aria-labelledby"?: string;
}

function OpenOnInputFocus({
  inputRef,
  shouldOpen,
}: {
  inputRef: RefObject<HTMLInputElement | null>;
  shouldOpen: boolean;
}) {
  const state = useContext(OverlayTriggerStateContext);
  const stateRef = useRef(state);
  const shouldOpenRef = useRef(shouldOpen);

  useEffect(() => {
    stateRef.current = state;
    shouldOpenRef.current = shouldOpen;
  }, [shouldOpen, state]);

  useEffect(() => {
    const input = inputRef.current;
    if (!input) {
      return;
    }

    let openTimer: number | undefined;
    const open = () => {
      // Let the initiating pointer event finish before mounting the popover.
      openTimer = window.setTimeout(() => {
        if (shouldOpenRef.current) {
          stateRef.current?.open();
        }
      }, 0);
    };
    input.addEventListener("focus", open);
    return () => {
      input.removeEventListener("focus", open);
      window.clearTimeout(openTimer);
    };
  }, [inputRef]);

  return null;
}

/**
 * A large hero-style autocomplete combobox with underline border and chevron.
 *
 * Features:
 * - Optionally opens on initial empty focus and filters as the user types
 * - Selection-only behavior (no custom free-text values)
 * - Relevance-ranked filtering with area grouping
 * - Clear button for explicit reset
 *
 * Reusable for any entity type — airports, cities, venues, categories, etc.
 */
export function HeroComboBox({
  label,
  sublabel,
  options,
  featuredOptions,
  showOptionsOnEmpty = true,
  isRequired = false,
  value,
  selectedCode,
  onChange,
  placeholder = "Search...",
  "aria-label": ariaLabel,
  "aria-labelledby": ariaLabelledBy,
}: HeroComboBoxProps) {
  const [inputValue, setInputValue] = useState(value);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const getDisplayValue = useCallback((option: HeroComboBoxOption | null) => {
    if (!option) {
      return "";
    }
    return `${option.label} [${option.code}]`;
  }, []);

  const airportByCode = useMemo(() => {
    const lookup = new Map<string, HeroComboBoxOption>();
    for (const option of options) {
      if (option.type !== "AREA" && option.code) {
        lookup.set(option.code, option);
      }
    }
    return lookup;
  }, [options]);

  const areaByCode = useMemo(() => {
    const lookup = new Map<string, HeroComboBoxOption>();
    for (const option of options) {
      if (option.type === "AREA" && option.code) {
        lookup.set(option.code, option);
      }
    }
    return lookup;
  }, [options]);

  const selectedOption = useMemo(() => {
    if (!selectedCode) {
      return null;
    }

    return (
      airportByCode.get(selectedCode) ?? areaByCode.get(selectedCode) ?? null
    );
  }, [airportByCode, areaByCode, selectedCode]);

  useEffect(() => {
    setInputValue(selectedOption ? getDisplayValue(selectedOption) : value);
  }, [getDisplayValue, selectedOption, value]);

  const getMatchScore = useCallback(
    (option: HeroComboBoxOption, query: string) => {
      const optionCode = option.code?.toLowerCase() ?? "";
      const optionLabel = option.label.toLowerCase();
      const optionDescription = option.description?.toLowerCase() ?? "";

      if (optionCode === query) {
        return 100;
      }

      if (optionCode.startsWith(query)) {
        return 80;
      }

      if (optionLabel.includes(query)) {
        return 60;
      }

      if (optionDescription.includes(query)) {
        return 40;
      }

      return 0;
    },
    [],
  );

  const filteredOptions = useMemo(() => {
    const query = inputValue.trim().toLowerCase();
    if (!query) {
      if (!showOptionsOnEmpty) {
        return [];
      }

      const featuredIds = new Set(featuredOptions?.map((option) => option.id));
      const featuredFirst = [
        ...(featuredOptions ?? []).filter((option) => option.type !== "AREA"),
        ...options.filter(
          (option) => option.type !== "AREA" && !featuredIds.has(option.id),
        ),
      ];

      return featuredFirst.slice(0, INITIAL_VISIBLE_RESULTS);
    }

    if (
      selectedOption &&
      query === getDisplayValue(selectedOption).toLowerCase()
    ) {
      return [];
    }

    type ResultBlock = {
      score: number;
      items: HeroComboBoxDisplayOption[];
      order: number;
      isAreaGroup: boolean;
    };

    const areaMatchedCodes = new Set<string>();
    const blocks: ResultBlock[] = [];
    let order = 0;

    for (const option of options) {
      if (option.type !== "AREA") {
        continue;
      }

      const areaScore = getMatchScore(option, query);
      if (!areaScore) {
        continue;
      }

      const children =
        option.constituentAirports
          ?.map((airportCode) => airportByCode.get(airportCode))
          .filter((child): child is HeroComboBoxOption => Boolean(child)) ?? [];

      const sortedChildren = children.sort((first, second) => {
        const secondScore = Math.max(
          AREA_CHILD_SCORE,
          getMatchScore(second, query),
        );
        const firstScore = Math.max(
          AREA_CHILD_SCORE,
          getMatchScore(first, query),
        );
        if (secondScore !== firstScore) {
          return secondScore - firstScore;
        }
        return first.label.localeCompare(second.label);
      });

      const groupedChildren = sortedChildren.map((child) => ({
        ...child,
        isGroupedChild: true,
      }));

      blocks.push({
        score: areaScore,
        items: [option, ...groupedChildren],
        order,
        isAreaGroup: true,
      });
      order += 1;

      if (option.code) {
        areaMatchedCodes.add(option.code);
      }
    }

    for (const option of options) {
      if (option.type === "AREA") {
        continue;
      }

      const score = getMatchScore(option, query);
      if (!score) {
        continue;
      }

      if (
        option.parentMetroCode &&
        areaMatchedCodes.has(option.parentMetroCode)
      ) {
        continue;
      }

      blocks.push({
        score,
        items: [option],
        order,
        isAreaGroup: false,
      });
      order += 1;
    }

    const sortedBlocks = blocks.sort((first, second) => {
      if (second.score !== first.score) {
        return second.score - first.score;
      }
      return first.order - second.order;
    });

    const results: HeroComboBoxDisplayOption[] = [];
    const addedIds = new Set<string>();

    for (const block of sortedBlocks) {
      const nextItems = block.items.filter((item) => !addedIds.has(item.id));
      if (!nextItems.length) {
        continue;
      }

      const willExceedLimit =
        results.length + nextItems.length > SEARCH_VISIBLE_RESULTS;
      if (
        results.length >= SEARCH_VISIBLE_RESULTS ||
        (willExceedLimit && !block.isAreaGroup)
      ) {
        break;
      }

      for (const item of nextItems) {
        addedIds.add(item.id);
        results.push(item);
      }
    }

    return results;
  }, [
    airportByCode,
    getDisplayValue,
    getMatchScore,
    inputValue,
    featuredOptions,
    options,
    selectedOption,
    showOptionsOnEmpty,
  ]);

  const handleInputChange = useCallback((val: string) => {
    setInputValue(val);
  }, []);

  const handleSelectionChange = useCallback(
    (key: React.Key | null) => {
      // React Aria fires onSelectionChange(null) when the user types and the
      // input no longer matches the selected item. We only want to propagate
      // null (deselect) when the user explicitly clears via handleClear.
      if (key === null) {
        return;
      }

      const selected = options.find((option) => option.id === String(key));
      if (!selected) {
        return;
      }

      setInputValue(getDisplayValue(selected));
      onChange(selected);
    },
    [getDisplayValue, options, onChange],
  );

  const handleClear = useCallback(() => {
    setInputValue("");
    onChange(null);
    inputRef.current?.focus();
  }, [onChange]);

  const emptyStateLabel = inputValue.trim()
    ? "No results found"
    : options.length
      ? "Start typing to search"
      : "No destinations available";
  const accessibleLabel = ariaLabel ?? label ?? sublabel;
  const clearLabel =
    label || sublabel || ariaLabel?.replace(/\s+city$/i, "") || "Selection";

  return (
    <label className="events-hero-field">
      {label ? <span>{label}</span> : null}
      <AriaComboBox
        aria-label={ariaLabelledBy ? undefined : accessibleLabel}
        aria-labelledby={ariaLabelledBy}
        isRequired={isRequired}
        inputValue={inputValue}
        selectedKey={selectedOption?.id ?? null}
        onInputChange={handleInputChange}
        onSelectionChange={handleSelectionChange}
        className="events-hero-combobox"
        menuTrigger="input"
        defaultFilter={() => true}
        allowsEmptyCollection
      >
        <OpenOnInputFocus
          inputRef={inputRef}
          shouldOpen={!selectedOption && showOptionsOnEmpty}
        />
        <div className="events-hero-field__wrapper">
          <AriaInput
            ref={inputRef}
            className="events-hero-field__input"
            placeholder={placeholder}
          />
          {selectedOption ? (
            <button
              type="button"
              className="events-hero-field__clear"
              onClick={handleClear}
              aria-label={`Clear ${clearLabel}`}
            >
              ×
            </button>
          ) : null}
        </div>
        <AriaPopover className="events-hero-combobox__popover" offset={8}>
          {filteredOptions.length > 0 ? (
            <AriaListBox
              className="events-hero-combobox__list"
              items={filteredOptions}
            >
              {(item) => (
                <AriaListBoxItem
                  id={item.id}
                  textValue={item.label}
                  className={`events-hero-combobox__item${item.type === "AREA" ? " events-hero-combobox__item--area" : ""}${"isGroupedChild" in item && item.isGroupedChild ? " events-hero-combobox__item--grouped-child" : ""}`}
                >
                  <div className="events-hero-combobox__item-main">
                    {item.code ? (
                      <strong className="events-hero-combobox__item-code">
                        {item.code}
                      </strong>
                    ) : null}
                    <span className="events-hero-combobox__item-label">
                      {item.label}
                    </span>
                    {item.description ? (
                      <span className="events-hero-combobox__item-desc">
                        {item.description}
                      </span>
                    ) : null}
                  </div>
                </AriaListBoxItem>
              )}
            </AriaListBox>
          ) : (
            <div className="events-hero-combobox__empty">{emptyStateLabel}</div>
          )}
        </AriaPopover>
      </AriaComboBox>
      {sublabel ? <strong>{sublabel}</strong> : null}
    </label>
  );
}
